// src/services/draftService.ts

import { apiClient } from "./apiClient";
import { PageResponse } from "../models/PaginationModel";
import type { EntryModel } from "../models/EntryModel";

/**
 * DTO returned by the backend for a single saved draft.
 * Mirrors EntryDraftResponse.java.
 *
 * Exactly one topic group will be populated:
 *   - match:        matchId, homeTeam, awayTeam, matchDate, sportType
 *   - person:       personId, personFullName, personSlug
 *   - club:         clubId, clubName, clubSlug
 *   - team:         teamId, teamName, teamClubSlug, teamSlug
 *   - custom topic: customTopicId, customTopicTitle, customTopicSlug
 */
export interface EntryDraft {
  id: number;
  content: string;
  createdAt: string; // ISO date-time
  updatedAt: string; // ISO date-time

  // Match draft fields
  matchId: number | null;
  homeTeam: string | null;
  awayTeam: string | null;
  matchDate: string | null; // ISO date (YYYY-MM-DD)
  sportType: string | null;

  // Person draft fields
  personId: number | null;
  personFullName: string | null;
  personSlug: string | null;

  // Club draft fields
  clubId: number | null;
  clubName: string | null;
  clubSlug: string | null;

  // Team draft fields
  teamId: number | null;
  teamName: string | null;
  teamClubSlug: string | null; // parent club slug — first URL segment in /takim/{clubSlug}/{teamSlug}
  teamSlug: string | null;     // sport+gender+teamCode slug — second URL segment
  teamCode: string | null;     // sub-team code for display label (e.g. "u19", "1b")

  // Custom topic ("gündem") draft fields
  customTopicId: number | null;
  customTopicTitle: string | null;
  customTopicSlug: string | null;
}

/**
 * Request body for creating a new draft.
 * Exactly one of matchId / personId / clubId / teamId / customTopicId must be set.
 */
export interface CreateDraftRequest {
  content: string;
  matchId?: number | null;
  personId?: number | null;
  clubId?: number | null;
  teamId?: number | null;
  customTopicId?: number | null;
}

/**
 * Request body for updating an existing draft.
 */
export interface UpdateDraftRequest {
  content: string;
}

// ───────────────────────── Queries ─────────────────────────

/**
 * Paginated list of the current user's drafts, most-recently-updated first.
 */
export const getDrafts = async (
  page: number,
  size: number
): Promise<PageResponse<EntryDraft>> => {
  return apiClient.get<PageResponse<EntryDraft>>(
    `/api/drafts?page=${page}&size=${size}`
  );
};

/**
 * Total count of the current user's drafts — used for badges.
 */
export const getDraftCount = async (): Promise<number> => {
  const res = await apiClient.get<{ count: number }>(`/api/drafts/count`);
  return res.count;
};

/**
 * Fetch a single draft by id.
 */
export const getDraft = async (id: number): Promise<EntryDraft> => {
  return apiClient.get<EntryDraft>(`/api/drafts/${id}`);
};

// ───────────────────────── Mutations ─────────────────────────

/**
 * Save a new draft. Exactly one of matchId / personId / clubId / teamId / customTopicId must be set.
 */
export const createDraft = async (
  body: CreateDraftRequest
): Promise<EntryDraft> => {
  return apiClient.post<EntryDraft>(`/api/drafts`, body);
};

/**
 * Update an existing draft's content.
 */
export const updateDraft = async (
  id: number,
  body: UpdateDraftRequest
): Promise<EntryDraft> => {
  return apiClient.put<EntryDraft>(`/api/drafts/${id}`, body);
};

/**
 * Permanently delete a single draft.
 */
export const deleteDraft = async (id: number): Promise<void> => {
  return apiClient.delete<void>(`/api/drafts/${id}`);
};

/**
 * Bulk-delete drafts. Ids that don't belong to the user are silently skipped.
 * Returns the number actually deleted.
 */
export const bulkDeleteDrafts = async (ids: number[]): Promise<number> => {
  const res = await apiClient.delete<{ deleted: number }>(`/api/drafts/bulk`, {
    ids,
  });
  return res.deleted;
};

/**
 * Publish a draft. On success the draft is deleted and the created entry
 * is returned. On failure (rate limit, duplicate, HTML) the draft is preserved
 * so the user can edit and retry.
 */
export const publishDraft = async (id: number): Promise<EntryModel> => {
  return apiClient.post<EntryModel>(`/api/drafts/${id}/publish`);
};